import Link from 'next/link';
import Image from 'next/image';
import { CSSProperties, FC } from 'react';
import classNames from 'classnames';
import { asText } from '@prismicio/client';
import { PrismicImageWithAlt } from '@/components';

import { PromoCarouselDocumentDataCarouselItemsItemWithID } from '@/types';
import { getCSSpropety } from '@/utils';

import style from './promocard.module.scss';

interface IPromoCard {
  className?: string;
  specialUrl?: string;
  special?: PromoCarouselDocumentDataCarouselItemsItemWithID;
}

const PromoCard: FC<IPromoCard> = ({ className, specialUrl, special }) => {
  if (!special) return null;

  const {
    background_color,
    title_color,
    description_color,
    cta_background,
    cta_text_color,
  } = special;
  const backgroundColor = getCSSpropety('backgroundColor', background_color);
  const titleColor = getCSSpropety('color', title_color);
  const ctaBackgroundColor = getCSSpropety('backgroundColor', cta_background);
  const ctaTextColor = getCSSpropety('color', cta_text_color);
  const descriptionColor = getCSSpropety('color', description_color);
  const ctaBorder = getCSSpropety(
    'border',
    !cta_background ? `1px solid ${cta_text_color}` : undefined,
  );

  const content = special?.definedInPrismic ? (
    <div
      className={classNames(style.promotion_card, className)}
      style={backgroundColor as CSSProperties}
    >
      <figure
        className={classNames(style.picture, {
          [style.picture_small]: !special?.pincture_only,
        })}
      >
        {special?.picture?.url && (
          <PrismicImageWithAlt
            defaultAlt={special?.title ?? 'Promotion image'}
            field={special?.picture}
            fill
          />
        )}
      </figure>
      {!special?.pincture_only && (
        <div className={style.content} style={backgroundColor as CSSProperties}>
          <div className={style.description}>
            <h3
              style={titleColor as CSSProperties}
              title={special?.title ?? ''}
            >
              {special?.title}
            </h3>
            <div
              className={style.extra_description}
              title={asText(special?.description) ?? ''}
            >
              <p style={descriptionColor as CSSProperties}>
                {asText(special?.description)}
              </p>
            </div>
          </div>

          {special?.cta_text && (
            <button
              aria-label={`Go to ${special?.title ?? 'promotion'}`}
              className={classNames(style.cta_button, {
                [style.cta_button_outlander]: !ctaBackgroundColor,
              })}
              style={{
                ...ctaBackgroundColor,
                ...ctaTextColor,
                ...ctaBorder,
              }}
            >
              {special?.cta_text}
            </button>
          )}
        </div>
      )}
    </div>
  ) : (
    <div className={classNames(style.promotion_card, className)}>
      <figure className={style.picture}>
        {special?.imageUrl ? (
          <Image
            alt={special.treezDiscountTitle ?? 'Promotion image'}
            fill
            src={special?.imageUrl}
          />
        ) : (
          <PrismicImageWithAlt
            defaultAlt={special.treezDiscountTitle ?? 'Promotion image'}
            field={special?.picture}
            fill
          />
        )}
      </figure>
      {!special.displayImageOnly && (
        <div
          className={style.content}
          style={special.imageUrl ? {} : { ...backgroundColor }}
        >
          <div className={style.description}>
            <h3 style={titleColor as CSSProperties}>
              {special.areTreezTitleAndDescriptionPresent
                ? special?.treezDiscountTitle
                : special.title}
            </h3>
            <div className={style.extra_description}>
              <p style={descriptionColor as CSSProperties}>
                {special.areTreezTitleAndDescriptionPresent
                  ? special?.treezDiscountDescription
                  : asText(special?.description)}
              </p>
            </div>
          </div>

          {special?.cta_text && !special.imageUrl && (
            <div
              className={classNames(style.cta_button, {
                [style.cta_button_outlander]: !ctaBackgroundColor,
              })}
              style={{
                ...ctaBackgroundColor,
                ...ctaTextColor,
                ...ctaBorder,
              }}
            >
              {special?.cta_text}
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (specialUrl) {
    return (
      <Link
        aria-label={`See details of ${special?.title ?? 'promotion'}`}
        href={specialUrl}
      >
        {content}
      </Link>
    );
  }

  return content;
};

export { PromoCard };
